import { CpuIcon, GlobeIcon, LeafIcon, ScaleIcon, type LucideIcon } from 'lucide-react';

import { AnalysisSubHeader, IconBadgeCard } from '../../../../components';

import { StatistaChartCard } from '../StatistaCharts';

import type { BreakthroughOpportunitiesProps } from './BreakthroughOpportunities.types';
import type { BreakthroughsDto } from '@inference.engine/api-client';
import { useTranslation } from 'react-i18next';

import { hasContent } from '../../../../utils/hasContent';

interface BreakthroughCard {
  key: keyof BreakthroughsDto;
  labelKey: string;
  icon: LucideIcon;
}

const BREAKTHROUGH_CARDS = [
  {
    key: 'technology',
    labelKey: 'analysisResult.breakthroughOpportunities.technology',
    icon: CpuIcon,
  },
  {
    key: 'sustainability',
    labelKey: 'analysisResult.breakthroughOpportunities.sustainability',
    icon: LeafIcon,
  },
  {
    key: 'regulation',
    labelKey: 'analysisResult.breakthroughOpportunities.regulation',
    icon: ScaleIcon,
  },
  {
    key: 'culture',
    labelKey: 'analysisResult.breakthroughOpportunities.culture',
    icon: GlobeIcon,
  },
] as const satisfies readonly BreakthroughCard[];

export const hasBreakthroughs = (breakthroughs: BreakthroughsDto | undefined) =>
  BREAKTHROUGH_CARDS.some((card) => hasContent(breakthroughs?.[card.key]));

export const BreakthroughOpportunities = ({
  breakthroughs,
  citedDatasets,
}: BreakthroughOpportunitiesProps) => {
  const { t } = useTranslation('analysis');

  const cards = BREAKTHROUGH_CARDS.filter((card) => hasContent(breakthroughs?.[card.key]));
  if (cards.length === 0) return null;

  return (
    <div className="flex flex-col gap-6">
      <AnalysisSubHeader
        title={t('analysisResult.breakthroughOpportunities.breakthroughOpportunities')}
        subtitle={t('analysisResult.breakthroughOpportunities.keyAreasForInnovationAnd')}
        subtitleTone="meta"
      />
      <div className="grid grid-cols-1 items-stretch gap-sm md:grid-cols-2">
        {cards.map((card) => (
          <IconBadgeCard
            key={card.key}
            icon={card.icon}
            title={t(card.labelKey)}
            description={breakthroughs?.[card.key]}
          />
        ))}
      </div>
      {citedDatasets?.map((cited) => (
        <StatistaChartCard key={cited.id} dataset={cited} citedIn={breakthroughs} />
      ))}
    </div>
  );
};
